import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Bot, Plus, Search, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  IconButton,
  Input,
  Modal,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  createDocument,
  DOC_TYPE_LABELS,
  type DocType,
} from '@/api/sapDocuments.ts'

interface CustomerResult {
  id: string
  name: string
  source: 'sap' | 'tanss'
  sapBpCode?: string
  tenantId?: string
  tenantCode?: string
}

interface EditableLine {
  itemCode: string
  description: string
  quantity: number
  unitPrice: number
  discountPct: number
}

const emptyLine = (): EditableLine => ({
  itemCode: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discountPct: 0,
})

type CreateDocType = Exclude<DocType, 'delivery'>

const CREATABLE_TYPES: CreateDocType[] = ['quotation', 'order', 'invoice', 'credit']

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialType?: CreateDocType
  onSuccess?: () => void
}

export function DocumentCreateModal({ open, onOpenChange, initialType = 'quotation', onSuccess }: Props) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [docType, setDocType] = useState<CreateDocType>(initialType)
  const [remarks, setRemarks] = useState('')
  const [lines, setLines] = useState<EditableLine[]>([emptyLine()])

  const [customerQuery, setCustomerQuery] = useState('')
  const [suggestions, setSuggestions] = useState<CustomerResult[]>([])
  const [selected, setSelected] = useState<CustomerResult | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) {
      setDocType(initialType)
      setRemarks('')
      setLines([emptyLine()])
      setCustomerQuery('')
      setSelected(null)
      setSuggestions([])
    }
  }, [open, initialType])

  useEffect(() => {
    if (!customerQuery.trim() || customerQuery.length < 2) {
      setSuggestions([])
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const res = await apiClient.get<CustomerResult[]>(
          `/quotes/customers/search?q=${encodeURIComponent(customerQuery)}`,
        )
        setSuggestions(res.data)
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
  }, [customerQuery])

  function updateLine(idx: number, patch: Partial<EditableLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  }
  function removeLine(idx: number) {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))
  }

  const { mutate: doCreate, isPending } = useMutation({
    mutationFn: () => {
      if (docType === 'quotation') {
        return apiClient.post('/quotes', {
          customerSapBpCode: selected?.sapBpCode ?? null,
          customerName: selected?.name ?? customerQuery,
          tenantId: selected?.tenantId ?? null,
          scenario: 'free',
          title: `Angebot für ${selected?.name ?? customerQuery}`,
        }).then((r) => {
          // POST /api/quotes liefert { data: { id, … } } — nicht { id }.
          // Der direkte r.data.id-Zugriff vorher hat undefined geliefert
          // und in der Folge zu `/auftraege/angebot/undefined` navigiert.
          const id = (r.data as { data?: { id?: string }; id?: string })?.data?.id
            ?? (r.data as { id?: string })?.id
          if (!id) throw new Error('Keine Quote-ID in der Antwort')
          return { _isQuote: true, id }
        })
      }
      return createDocument({
        docType,
        cardCode: selected?.sapBpCode ?? '',
        tenantId: selected?.tenantId ?? undefined,
        positions: lines.map((l) => ({
          itemCode: l.itemCode,
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discountPct: l.discountPct,
        })),
        remarks,
      })
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })
      if ((data as { _isQuote?: boolean })._isQuote) {
        navigate(`/auftraege/angebot/${(data as { id: string }).id}`) // KI-Dialog liegt weiterhin unter /auftraege/angebot
        onOpenChange(false)
        return
      }
      toast({
        title: `${DOC_TYPE_LABELS[docType]} erstellt`,
        tone: 'success',
      })
      onSuccess?.()
      onOpenChange(false)
    },
    onError: (err) => {
      toast({
        title: 'Fehler beim Erstellen',
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        tone: 'danger',
      })
    },
  })

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice * (1 - l.discountPct / 100), 0)
  const isQuote = docType === 'quotation'
  const canSubmit = !isPending && (selected?.sapBpCode || (isQuote && customerQuery.trim()))

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title="Neues Dokument erstellen"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-body font-medium text-gray-700">
            Gesamt: {total.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={() => doCreate()} disabled={!canSubmit}>
              {isPending
                ? 'Wird erstellt…'
                : isQuote
                  ? 'Angebot erstellen + KI öffnen'
                  : `${DOC_TYPE_LABELS[docType]} erstellen`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Typ-Auswahl */}
        <div className="flex items-center gap-3">
          <label className="text-body font-medium text-gray-700 w-20 shrink-0">Typ</label>
          <SelectRoot value={docType} onValueChange={(v) => setDocType(v as CreateDocType)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREATABLE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {DOC_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          {isQuote && (
            <span className="text-caption text-blue-600 flex items-center gap-1">
              <Bot className="w-3.5 h-3.5" />
              KI-Assistent wird nach Erstellung geöffnet
            </span>
          )}
        </div>

        {/* Kunden-Suche */}
        <div className="relative flex items-start gap-3">
          <label className="text-body font-medium text-gray-700 w-20 shrink-0 pt-2">Kunde</label>
          <div className="flex-1 relative">
            {selected ? (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div>
                  <p className="text-body font-medium">{selected.name}</p>
                  <p className="text-caption text-gray-500">
                    {[selected.sapBpCode, selected.tenantCode].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button variant="ghost" size="xs" onClick={() => { setSelected(null); setCustomerQuery('') }}>
                  Ändern
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    className="pl-8"
                    placeholder="Kunde suchen…"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  />
                  {searchLoading && (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-caption text-gray-400">…</span>
                  )}
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-card max-h-48 overflow-y-auto">
                    {suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                        onMouseDown={() => { setSelected(s); setCustomerQuery(s.name); setShowSuggestions(false) }}
                      >
                        <span className="text-body">{s.name}</span>
                        <span className="text-caption text-gray-400">
                          {[s.sapBpCode, s.tenantCode].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Positionen (nicht bei Angeboten — KI uebernimmt) */}
        {!isQuote && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-body font-medium text-gray-700">Positionen</label>
            </div>
            <div className="grid grid-cols-[1fr_2fr_70px_100px_70px_32px] gap-1.5 text-caption font-medium text-gray-500 px-1">
              <span>Artikelnr.</span>
              <span>Beschreibung</span>
              <span>Menge</span>
              <span>Preis</span>
              <span>Rabatt %</span>
              <span />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_2fr_70px_100px_70px_32px] gap-1.5 items-center">
                  <Input
                    value={line.itemCode}
                    onChange={(e) => updateLine(idx, { itemCode: e.target.value })}
                    placeholder="z.B. ITEM-001"
                    className="py-1 text-sm"
                  />
                  <Input
                    value={line.description}
                    onChange={(e) => updateLine(idx, { description: e.target.value })}
                    placeholder="Bezeichnung"
                    className="py-1 text-sm"
                  />
                  <Input
                    type="number"
                    value={String(line.quantity)}
                    onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })}
                    min={0}
                    className="py-1 text-sm"
                  />
                  <Input
                    type="number"
                    value={String(line.unitPrice)}
                    onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                    step={0.01}
                    min={0}
                    className="py-1 text-sm"
                  />
                  <Input
                    type="number"
                    value={String(line.discountPct)}
                    onChange={(e) => updateLine(idx, { discountPct: Number(e.target.value) })}
                    min={0}
                    max={100}
                    className="py-1 text-sm"
                  />
                  <IconButton
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    aria-label="Entfernen"
                    variant="ghost"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                  />
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setLines((p) => [...p, emptyLine()])} className="gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Position hinzufügen
            </Button>
          </div>
        )}

        {/* Bemerkungen */}
        <div className="flex items-start gap-3">
          <label className="text-body font-medium text-gray-700 w-20 shrink-0 pt-2">Bemerkung</label>
          <Textarea
            className="flex-1"
            rows={2}
            placeholder="Interne Notiz oder Betreff…"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
